import React, { useCallback, useEffect, useRef, useState } from 'react';

import BadgeUrl from './assets/star.svg';
import './BannerDiscount.scss';

import { PAYLOAD_PROPS } from '@wetransfer/transfer/constant/experiments/discount-banner';
import Helper from '@wetransfer/transfer/helper';
import Analytics from '@wetransfer/transfer/modules/analytics';
import { trackAmplitudeEvent } from '@wetransfer/transfer/modules/analytics/vendors/amplitude';
import I18n from '@wetransfer/transfer/modules/i18n';
import Banner from '@wetransfer/transfer/oneui/react/Banner';

interface Props {
  payload: Array<PAYLOAD_PROPS>;
}

const BannerDiscount = ({ payload }: Props): JSX.Element => {
  const [showBanner, setShowBanner] = useState(true);
  const discountData = Helper.getCurrentExperimentData(payload);

  const discountRate = discountData?.percentageDiscount;
  const discountTitle = discountData?.campaignTitle;
  const discountCode = discountData?.couponCode;

  const mainCtaUrl = discountCode
    ? `/pricing?coupon=${discountCode}`
    : '/pricing';

  const displayBanner = discountData && showBanner;

  const textContent = I18n.t('discount.banner.title', {
    discount_amount: discountRate,
  });
  const ref = useRef<HTMLDivElement>(null);

  const doc = document.querySelectorAll<HTMLElement>('.application')[0];

  const amplitudeTracking = useCallback(
    (action: string) => {
      trackAmplitudeEvent({
        action: 'Upsell',
        additionalProperties: {
          Type: 'Banner',
          Context: `Discount Campaign: ${discountTitle}`,
          Action: action,
        },
      });
    },
    [discountTitle]
  );

  useEffect(() => {
    if (displayBanner) {
      Analytics.trackSnowplowEvent({
        category: 'upsell',
        action: 'show_banner',
        property: 'black_friday',
      });

      amplitudeTracking('Shown');
    }
  }, [amplitudeTracking, displayBanner]);

  const dismissBanner = useCallback(() => {
    doc.style.height = '100%';
    setShowBanner(false);
  }, [doc.style]);

  const onClickDismissButton = useCallback(() => {
    dismissBanner();

    Analytics.trackSnowplowEvent({
      category: 'upsell',
      action: 'click_dismiss_banner',
      property: 'black_friday',
    });
    amplitudeTracking('Dismiss');
  }, [amplitudeTracking, dismissBanner]);

  const onClickCta = useCallback(() => {
    dismissBanner();

    Analytics.trackSnowplowEvent({
      category: 'upsell',
      action: 'click_upgrade_banner',
      property: 'black_friday',
    });
    amplitudeTracking('Upgrade');
  }, [amplitudeTracking, dismissBanner]);

  return (
    <>
      {displayBanner && (
        <Banner
          bannerRef={ref}
          redirectionUrl={mainCtaUrl}
          ctaLabel={I18n.t('discount.banner.cta_label')}
          dismissBanner={onClickDismissButton}
          onClickCta={onClickCta}
        >
          <>
            <div className="Banner-discount__badge">
              <span>-{discountRate}%</span>
              <img src={BadgeUrl} alt="Badge" />
            </div>
            <div className="Banner-discount__text-content">
              <p>
                <span className="Banner-discount__title">{discountTitle}</span>
                <span> </span>
                <span>{textContent}</span>
              </p>
            </div>
          </>
        </Banner>
      )}
    </>
  );
};

export default BannerDiscount;
